import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DisplayedSize, Product, Size, SizeStructure } from 'app/models/menu.model';
import { Organization } from 'app/models/organization.model';
import { ManualSizeCreateDto, ManualSizeUpdateDto, SizeService } from 'app/services/size.service';
import { Store } from '@ngrx/store';
import * as MenuSelectors from 'app/reducers/menu.selectors';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom, Observable } from 'rxjs';

/**
 * Создание/редактирование ручного размера: правки уезжают напрямую в БД geco.pos
 * (api/front/size), в отличие от размеров из POS-выгрузки, которые правятся оверайдами.
 */
@UntilDestroy()
@Component({
  selector: 'app-edit-manual-size-modal',
  templateUrl: './edit-manual-size-modal.component.html',
  styleUrls: ['./edit-manual-size-modal.component.scss']
})
export class EditManualSizeModalComponent implements OnInit {
  form: FormGroup;
  isSaving = false;
  errorMessage = '';

  products: Product[] = [];
  filteredProducts: Product[] = [];
  private products$: Observable<Map<string, Product>>;

  constructor(
    public dialogRef: MatDialogRef<EditManualSizeModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditManualSizeModalComponentDataType,
    private sizeService: SizeService,
    private store: Store,
  ) {
  }

  get isEditMode(): boolean {
    return !!this.data.size;
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      name: new FormControl(this.data.size?.name ?? '', [Validators.required]),
      price: new FormControl(this.data.sizeStructure?.price ?? 0, [Validators.required, Validators.min(0)]),
      weight: new FormControl(this.data.sizeStructure?.weight ?? 0, [Validators.min(0)]),
      isHidden: new FormControl(this.data.sizeStructure?.isHidden ?? false),
      externalPictureUrl: new FormControl(this.data.sizeStructure?.externalPictureUrl ?? ''),
      linkedProductSearch: new FormControl(''),
    });

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        const nameValue = this.data.size?.nameTranslations?.[lang] ?? '';
        this.form.addControl(this.getFormLangName(lang), new FormControl(nameValue, []));
      }
    }

    this.products$ = this.store.select(MenuSelectors.selectDictionaryProducts);
    this.products$
      .pipe(untilDestroyed(this))
      .subscribe(products => {
        this.products = [...products.values()];
        this.filterProducts(this.linkedProductName());
        const linkedId = this.data.size?.linkedProductExternalId;
        if (linkedId && !this.form.value['linkedProductSearch']) {
          this.form.patchValue({ linkedProductSearch: products.get(linkedId)?.name ?? linkedId });
        }
      });

    this.form.get('linkedProductSearch').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => this.filterProducts(value));
  }

  getFormLangName(lang: string): string {
    return 'lang_' + lang;
  }

  onPictureChanged(url: string) {
    this.form.patchValue({ externalPictureUrl: url });
  }

  clearLinkedProduct() {
    this.form.patchValue({ linkedProductSearch: '' });
  }

  displayedProducts(): Product[] {
    return this.filteredProducts.slice(0, 50);
  }

  async submit() {
    if (!this.form.valid || this.isSaving) {
      return;
    }

    const nameTranslations = {};
    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        nameTranslations[lang] = this.form.value[this.getFormLangName(lang)] ?? '';
      }
    }

    const updateDto: ManualSizeUpdateDto = {
      name: this.form.value['name'].trim(),
      nameTranslations,
      price: this.form.value['price'],
      weight: this.form.value['weight'] ?? 0,
      isHidden: this.form.value['isHidden'],
      externalPictureUrl: this.form.value['externalPictureUrl'] || null,
      linkedProductExternalId: this.linkedProductExternalId(),
    };

    this.isSaving = true;
    this.errorMessage = '';
    try {
      const size: Size = this.isEditMode
        ? await firstValueFrom(this.sizeService.updateSize(this.data.size.externalId, updateDto))
        : await firstValueFrom(this.sizeService.createSize({
          ...updateDto,
          organizationId: this.data.organizationId,
          productExternalId: this.data.productExternalId,
        } as ManualSizeCreateDto));

      this.dialogRef.close({
        size,
        structure: {
          price: updateDto.price,
          weight: updateDto.weight,
          isHidden: updateDto.isHidden,
          externalPictureUrl: updateDto.externalPictureUrl,
        },
      } as EditManualSizeModalResult);
    } catch (error) {
      this.errorMessage = error?.error ?? error?.message ?? 'Error';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Привязка опциональна: берём продукт только при точном совпадении имени из списка,
   * пустое поле = «без привязки».
   */
  private linkedProductExternalId(): string | null {
    const search = (this.form.value['linkedProductSearch'] ?? '').trim();
    if (!search) {
      return null;
    }

    const linkedId = this.data.size?.linkedProductExternalId;
    if (linkedId && (search === linkedId || search === this.products.find(p => p.externalId === linkedId)?.name)) {
      return linkedId;
    }

    const product = this.products.find(p => p.name === search || p.externalId === search);
    return product?.externalId ?? null;
  }

  private linkedProductName(): string {
    return this.form?.value['linkedProductSearch'] ?? '';
  }

  private filterProducts(search: string) {
    const query = (search ?? '').toLowerCase().trim();
    const products = this.products.filter(p => p.externalId !== this.data.productExternalId);
    this.filteredProducts = query
      ? products.filter(p => p.name?.toLowerCase().includes(query))
      : products;
  }
}

export interface EditManualSizeModalComponentDataType {
  organization: Organization;
  organizationId: string;
  productExternalId: string;
  size?: DisplayedSize;
  sizeStructure?: SizeStructure;
}

export interface EditManualSizeModalResult {
  size: Size;
  structure: Pick<SizeStructure, 'price' | 'weight' | 'isHidden' | 'externalPictureUrl'>;
}
